import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { NestedTranslateButton } from '@/components/common/translations/NestedTranslateButton'
import {
  isEditorChecklistAnswerItemArray,
  nextChecklistItemEditorId,
  type EditorChecklistAnswerItem,
} from '@/lib/action-modules/registry/subtypes/checklist'
import type { PropertyEditorProps } from '@/lib/action-modules/registry/subtypes/types'
import type { SubtypeState, SubtypeTranslations } from '@/lib/action-modules/registry/types'
import { checklistItemTextProperty } from '../../subtypeTranslations'

/** The reward an item pays out per answer state (`ChecklistItemAnswer`), in the order shown. */
const REWARD_FIELDS = [
  { field: 'negativeCheckedReward', labelKey: 'tabSubtypes.checklistItemRewards.negative' },
  { field: 'positiveCheckedReward', labelKey: 'tabSubtypes.checklistItemRewards.positive' },
  { field: 'notCheckedReward', labelKey: 'tabSubtypes.checklistItemRewards.notChecked' },
] as const

type RewardField = (typeof REWARD_FIELDS)[number]['field']

function hasAnyReward(items: EditorChecklistAnswerItem[]): boolean {
  return items.some(item => REWARD_FIELDS.some(({ field }) => item[field] !== 0))
}

export function ChecklistItemsEditor(props: PropertyEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const value = props.value
  if (!isEditorChecklistAnswerItemArray(value)) return <>{t('editor.invalidValue')}</>
  return (
    // Keyed so selecting another building block remounts the editor: `showRewards` starts from
    // the newly selected block's own items instead of keeping the previous block's answer. The
    // settings pane itself keeps one editor per property *name* mounted across that switch.
    <Editor
      key={props.subtype.editorId}
      value={value}
      onChanged={v => props.onChanged(v)}
      subtype={props.subtype}
      translations={props.translations}
    />
  )
}

function Editor({
  value,
  onChanged,
  subtype,
  translations,
}: {
  value: EditorChecklistAnswerItem[]
  onChanged: (v: EditorChecklistAnswerItem[]) => void
  subtype: SubtypeState
  translations: SubtypeTranslations
}) {
  const { t } = useTranslation('action-modules-wizard')
  // A small drag threshold so a plain click on the handle still isn't read as a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [activeId, setActiveId] = useState<number | null>(null)
  // View state only — three number fields per item overwhelm a checklist that doesn't reward per
  // item. Starts open when this block already carries rewards, so configured values are never
  // hidden; the initial guess is per block (see the `key` above).
  const [showRewards, setShowRewards] = useState(() => hasAnyReward(value))

  const onDelete = useCallback((item: EditorChecklistAnswerItem) => onChanged(value.filter(v => v !== item)), [value, onChanged])

  const onAdd = useCallback(() => {
    onChanged([
      ...value,
      {
        negativeCheckedReward: 0,
        notCheckedReward: 0,
        positiveCheckedReward: 0,
        text: '',
        id: null,
        defaultCheckListAnswer: 'NotChecked',
        // Not authoritative — the array position is, and `checklistItemsToModel` renumbers
        // `order` from it on save. Set only because the model requires the field.
        order: value.length,
        editorId: nextChecklistItemEditorId(value)
      }])
  }, [value, onChanged])

  const onItemChanged = useCallback(
    (item: EditorChecklistAnswerItem, next: EditorChecklistAnswerItem) => onChanged(value.map(v => (v === item ? next : v))),
    [value, onChanged],
  )

  const onDragStart = useCallback((event: DragStartEvent) => setActiveId(Number(event.active.id)), [])

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = value.findIndex(v => v.editorId === active.id)
      const newIndex = value.findIndex(v => v.editorId === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      onChanged(arrayMove(value, oldIndex, newIndex))
    },
    [value, onChanged],
  )

  const activeItem = activeId == null ? undefined : value.find(v => v.editorId === activeId)

  return (
    // Nested inside the subtypes tab's DndContext (see SubtypesTab): dnd-kit binds activators
    // through the `listeners` a row receives from its nearest context, so an item handle only ever
    // starts this inner drag — the tab's toolbox→preview context never sees the activation.
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {/* Wider gap only while the rewards are shown — collapsed, the items are single rows again
          and the tighter spacing keeps the list compact. */}
      <div className={`flex flex-col items-stretch ${showRewards ? 'gap-2' : 'gap-1'}`}>
        <SortableContext items={value.map(v => v.editorId)} strategy={verticalListSortingStrategy}>
          {value.map(item => (
            <ItemRow
              key={item.editorId}
              item={item}
              subtype={subtype}
              translations={translations}
              onTextChanged={text => onItemChanged(item, { ...item, text })}
              onRewardChanged={(field, reward) => onItemChanged(item, { ...item, [field]: reward })}
              onDelete={() => onDelete(item)}
              showRewards={showRewards}
            />
          ))}
        </SortableContext>
        {/* ml-7 (not pl-7) on both: margin moves the button's own edge, padding would only shift
            its label. Same offset as the rows above, so button, checkbox, item title and first
            reward field all share one left edge. */}
        <Button type="button" variant="outline" size="sm" className="ml-7 self-start" onClick={onAdd}>
          <Plus className="size-4" /> {t('buttons.add')}
        </Button>
        {/* Deliberately lighter than the property labels above (`text-sm font-medium`): this
            toggles what the form shows, it is not part of the module's configuration. */}
        <Label className="ml-7 self-start font-normal text-muted-foreground">
          <Checkbox checked={showRewards} onCheckedChange={c => setShowRewards(c === true)} />
          {t('tabSubtypes.checklistItemRewards.toggle')}
        </Label>
      </div>

      {/* The dragged row is rendered here instead of moving in place. Without an overlay dnd-kit
          translates the row itself by the raw pointer delta (both axes), which grows the settings
          pane's scroll area and makes its scrollbars appear mid-drag. The overlay is
          position:fixed, so it is outside that pane's layout entirely. */}
      <DragOverlay>
        {activeItem && (
          <div className="pointer-events-none flex flex-col gap-1 rounded-md border bg-card shadow-lg">
            <RowContent
              item={activeItem}
              subtype={subtype}
              translations={translations}
              showRewards={showRewards}
              dragging
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

function ItemRow({
  item,
  subtype,
  translations,
  onTextChanged,
  onRewardChanged,
  onDelete,
  showRewards,
}: {
  item: EditorChecklistAnswerItem
  subtype: SubtypeState
  translations: SubtypeTranslations
  onTextChanged: (text: string) => void
  onRewardChanged: (field: RewardField, reward: number) => void
  onDelete: () => void
  showRewards: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.editorId,
  })

  return (
    <div
      ref={setNodeRef}
      // While dragging, hide the row in place (the DragOverlay shows it) so its slot stays a
      // constant-height gap — same handling as the subtype rows in SubtypePreview.
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 }}
      className="flex flex-col gap-1"
    >
      <RowContent
        item={item}
        subtype={subtype}
        translations={translations}
        onTextChanged={onTextChanged}
        onRewardChanged={onRewardChanged}
        onDelete={onDelete}
        showRewards={showRewards}
        handleProps={{ ...listeners, ...attributes }}
      />
    </div>
  )
}

/** One item row's contents: drag handle, text, its optional translation button and delete, plus the
 *  per-answer-state rewards below. Shared by the sortable row and the drag overlay's floating copy,
 *  so both look identical. */
function RowContent({
  item,
  subtype,
  translations,
  onTextChanged,
  onRewardChanged,
  onDelete,
  showRewards,
  handleProps,
  dragging,
}: {
  item: EditorChecklistAnswerItem
  subtype: SubtypeState
  translations: SubtypeTranslations
  onTextChanged?: (text: string) => void
  onRewardChanged?: (field: RewardField, reward: number) => void
  onDelete?: () => void
  showRewards: boolean
  handleProps?: Record<string, unknown>
  dragging?: boolean
}) {
  const { t } = useTranslation('action-modules-wizard')

  // The translation row is keyed by subtype id + item id, so a row is only translatable
  // once both were persisted — same rule as the condition editors.
  const entityId = translations.entityId
  const property =
    entityId && subtype.subtype.id != null && item.id != null
      ? checklistItemTextProperty(subtype.subtype.id, item.id)
      : null

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Dragging starts here only, so the text field stays editable. touch-none because the
            PointerSensor needs the browser to stop treating the gesture as a scroll. */}
        <div
          {...handleProps}
          className={`shrink-0 touch-none p-1 text-muted-foreground ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <GripVertical className="size-4" />
        </div>
        <Input value={item.text ?? ""} onChange={e => onTextChanged?.(e.target.value)} />
        {property && entityId && (
          <NestedTranslateButton
            entityType={LocalizationEntityType.ActionModule}
            entityId={entityId}
            property={property}
            edited={translations.get(property)}
            onChange={v => translations.set(property, v)}
            dialogTitle={t('tabSubtypes.checklistItemTranslateTitle')}
            plannedLanguages={translations.getPlanned(property)}
            onPlannedChange={v => translations.setPlanned(property, v)}
            onLoaded={v => translations.setServerLoaded(property, v)}
          />
        )}
        <Button type="button" variant="ghost" size="icon" onClick={onDelete}>
          <Trash2 className="size-4" />
        </Button>
      </div>
      {/* Indented to line up with the text field above, so the three rewards read as belonging
          to that item. */}
      {showRewards && (
        <div className="grid grid-cols-3 gap-1 pl-7">
          {REWARD_FIELDS.map(({ field, labelKey }) => (
            <RewardInput
              key={field}
              label={t(labelKey)}
              value={item[field]}
              onChanged={reward => onRewardChanged?.(field, reward)}
            />
          ))}
        </div>
      )}
    </>
  )
}

/** One reward field. The typed text is kept locally while the field has focus so intermediate
 *  states a number input reports as empty (`''`, a lone `-`) don't get committed as a value and
 *  snap the caret back — only the parsed number leaves, and an unparsable field counts as 0. */
function RewardInput({
  label,
  value,
  onChanged,
}: {
  label: string
  value: number
  onChanged: (reward: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <Label className="flex flex-col items-stretch gap-1 text-xs font-normal text-muted-foreground">
      {label}
      <Input
        type="number"
        className="px-2"
        value={draft ?? String(value)}
        onChange={e => {
          setDraft(e.target.value)
          const parsed = Number(e.target.value)
          onChanged(e.target.value === '' || Number.isNaN(parsed) ? 0 : parsed)
        }}
        onBlur={() => setDraft(null)}
      />
    </Label>
  )
}
